import {
  AssignmentState,
  SectionState,
} from '@breakoutlearning/firebase-repository/types'
import classNames from 'classnames'
import { BreakoutPill } from 'components/design-system/BreakoutPill'
import { CheckIcon } from 'components/icons/Check'
import { TriangleAlertIcon } from 'components/icons/TriangleAlert'
import { FullPageSpinner } from 'components/Spinner'
import { useInstructorAssignmentCubit } from 'hooks/cubits/instructorAssignment'
import { useInstructorSectionCubit } from 'hooks/cubits/instructorSection'
import { useRootStore } from 'hooks/rootStore'
import { useTranslationTyped } from 'i18n/i18n'
import { observer } from 'mobx-react-lite'
import { useCallback, useLayoutEffect, useState } from 'react'
import { Trans } from 'react-i18next'

export const CanvasConfirmationPage = observer(
  function CanvasConfirmationPage() {
    const assignmentCubit = useInstructorAssignmentCubit()
    const sectionCubit = useInstructorSectionCubit()
    const { tt } = useTranslationTyped()
    const rootStore = useRootStore()
    const sectionState = sectionCubit.section.data?.sectionState
    const sectionPaymentMethod = sectionCubit.section.data?.sectionPaymentMethod
    const assignmentState = assignmentCubit.assignment.data?.assignmentState

    const [showConfirmationPage, setShowConfirmationPage] = useState(false)

    const onClick = useCallback(async () => {
      if (
        sectionState === SectionState.unconfigured ||
        sectionState === SectionState.notStarted
      ) {
        await sectionCubit.markAsInProgress()
      }
      if (assignmentState === AssignmentState.unconfigured) {
        await assignmentCubit.markAsActive()
      }

      // in regular mode, we will redirect to the assignment page
      rootStore.navigateToInstructorAssignmentPage(
        assignmentCubit.sectionId,
        assignmentCubit.assignmentId
      )
    }, [
      sectionState,
      assignmentState,
      rootStore,
      assignmentCubit,
      sectionCubit,
    ])

    const assignment = assignmentCubit.assignment
    const hasGrading = assignment.hasGrading
    const expiresAt = assignment.data.expiresAt
    const expiresAtMissing = !expiresAt
    const lineItemMissing = !assignment.data.ltiMaxScore
    const lineItemIsSet = !lineItemMissing
    const shouldBlockOnGrade = assignment.hasGrading && lineItemMissing
    const expiresAtIsInThePast = expiresAt && expiresAt < new Date()
    const expiresAtIsValid = !expiresAtMissing && !expiresAtIsInThePast

    useLayoutEffect(() => {
      if (sectionPaymentMethod === undefined) return
      if (!assignmentCubit.allDataLoaded) return
      if (!assignment.hasGrading && expiresAtIsValid) {
        onClick()
        return
      }
      if (expiresAtIsValid && !shouldBlockOnGrade) {
        onClick()
        return
      }
      setShowConfirmationPage(true)
    }, [
      sectionPaymentMethod,
      assignmentCubit.allDataLoaded,
      assignment.hasGrading,
      expiresAtIsValid,
      lineItemIsSet,
      shouldBlockOnGrade,
      onClick,
    ])

    if (!assignmentCubit.assignment || !assignmentCubit.allDataLoaded) {
      return <FullPageSpinner reason="canvas_confirmation_data_loading" />
    }

    if (!showConfirmationPage) {
      return <FullPageSpinner reason="canvas_confirmation_redirecting" />
    }

    return (
      <div className="lti-configuration min-h-full w-full">
        <div className="">
          <img
            src="/assets/images/logo.png"
            width={64}
            className="absolute right-8 top-8"
            alt={tt.breakout_learning()}
          />
        </div>
        <div className="lti-plant flex min-h-full w-full flex-col items-center justify-center">
          <div className="max-w-[800px] text-center">
            <div className="mb-2 text-headline-large">
              {tt.lti.configuration.invalid_canvas_title()}
            </div>
            <div className="mb-4 text-body-large">
              {tt.lti.configuration.invalid_canvas_description()}
            </div>
            <div
              className={classNames('', {
                'grid grid-cols-2 gap-2': hasGrading,
                'flex flex-row justify-center': !hasGrading,
              })}
            >
              {/* Dates Box */}
              <div className="flex max-w-[360px] flex-col justify-between rounded-2xl bg-core-tertiary px-8 py-7">
                <div className="text-body-large">
                  <div className="mb-2 flex flex-row items-center justify-between">
                    <div className="text-title-large">
                      {tt.lti.configuration.brightspace_add_dates()}
                    </div>
                    <BreakoutPill
                      label={tt.lti.configuration.required()}
                      kind="primary"
                      dot
                      dotClassName="bg-core-success"
                    />
                  </div>
                  <div className="mb-2 text-left text-body-large">
                    <Trans
                      i18nKey="lti.configuration.canvas_dates_instructions"
                      components={{
                        p: <p className="my-2 text-body-large" />,
                        b: <strong />,
                        ul: <ul />,
                        ol: <ol className="ml-4 list-decimal" />,
                        li: <li className="pb-1" />,
                      }}
                    />
                  </div>
                  {expiresAtMissing && (
                    <div className="flex flex-row items-center gap-1 text-left text-label-large text-core-error">
                      <TriangleAlertIcon
                        className="stroke-core-error"
                        size={16}
                      />
                      {tt.lti.configuration.brightspace_dates_warning()}
                    </div>
                  )}
                  {expiresAtIsInThePast && (
                    <div className="flex flex-row items-center gap-1 text-left text-label-large text-core-error">
                      <TriangleAlertIcon
                        className="stroke-core-error"
                        size={16}
                      />
                      {tt.lti.configuration.brightspace_expires_at_is_in_past()}
                    </div>
                  )}
                  {!expiresAtMissing && !expiresAtIsInThePast && (
                    <div className="flex flex-row items-center gap-1 text-left text-label-large text-core-success">
                      <CheckIcon className="stroke-core-success" size={16} />
                      {tt.lti.configuration.canvas_dates_detected()}
                    </div>
                  )}
                </div>
              </div>
              {/* Line Item Box */}
              {hasGrading && (
                <div className="flex max-w-[360px] flex-col justify-between rounded-2xl bg-core-tertiary px-8 py-7">
                  <div className="text-body-large">
                    <div className="mb-2 flex flex-row items-center justify-between">
                      <div className="text-title-large">
                        {tt.lti.configuration.canvas_add_grade_item()}
                      </div>
                      <BreakoutPill
                        label={tt.lti.configuration.required()}
                        kind="primary"
                        dot
                        dotClassName="bg-core-success"
                      />
                    </div>
                    <div className="mb-2 text-left text-body-large">
                      <Trans
                        i18nKey="lti.configuration.canvas_add_grade_item_instructions"
                        components={{
                          p: <p className="my-2 text-body-large" />,
                          b: <strong />,
                          ul: <ul />,
                          ol: <ol className="ml-4 list-decimal" />,
                          li: <li className="pb-1" />,
                        }}
                      />
                    </div>
                  </div>
                  {lineItemMissing && (
                    <div className="flex flex-row items-center gap-1 text-left text-label-large text-core-error">
                      <TriangleAlertIcon
                        className="stroke-core-error"
                        size={16}
                      />
                      {tt.lti.configuration.canvas_grade_item_missing()}
                    </div>
                  )}
                  {!lineItemMissing && (
                    <div className="flex flex-row items-center gap-1 text-left text-label-large text-core-success">
                      <CheckIcon className="stroke-core-success" size={16} />
                      {tt.lti.configuration.canvas_grade_item_detected()}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    )
  }
)
